/* eslint-disable no-console */
import React, { useState, useRef, useEffect } from 'react';
import { useGlobal } from '@rcg/ui-react';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import { formatPrice } from '@rcg/pricing-format';
import useStyles from '../CheckoutCard/useStyles';
import paymentStyles from './paymentStyles';
import Dictionary from '../../utils/dictionary';

import { trackPaymentView } from '../../utils/analytics/searchTracking';

const PaymentOption = ({
  holdFlightData,
  extraData,
  bookingProfile,
  loading,
  onPropertyChage = () => {},
  isRefund,
  setCreditCardFormRender = () => {},
}) => {
  const classes = useStyles();
  const paymentClasses = paymentStyles();
  const {
    renderContent,
    data: { localizedDayjs },
  } = useGlobal();

  const [selectedPayment, setselectedPayment] = useState('reserve');

  const { returnFlight } = extraData;
  const { priceChange } = holdFlightData;
  const checkoutRef = useRef(null);

  const handleOnChangePaymentOption = (event) => {
    onPropertyChage(event.target.value);
    setselectedPayment(event.target.value);
    if (event.target.value === 'payNow') {
      setTimeout(() => {
        if (checkoutRef.current) {
          checkoutRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 100);
    }
  };

  const { currency } = returnFlight.cabin.totalPrice;
  const payAmount = priceChange
    ? formatPrice(priceChange.newTotal.value, currency, true, true, true)
    : formatPrice(returnFlight.cabin.totalPrice.value, currency, true, true, true);
  const { isPaymentRequired, isRefundable } = returnFlight.cabin;
  const dueDate = localizedDayjs(bookingProfile.flightProfile.booking.finalPaymentDate).format(
    'MMM D[,] YYYY',
  );

  const fullPaymentDueToday =
    new Date(bookingProfile.flightProfile.booking.finalPaymentDate) <= new Date();

  useEffect(() => {
    if (!loading) {
      if (isPaymentRequired || fullPaymentDueToday) {
        const payNow = 'payNow';
        onPropertyChage(payNow);
        setselectedPayment(payNow);
        trackPaymentView(true);
      } else {
        trackPaymentView(false);
      }
    }
    // eslint-disable-next-line
  }, [loading]);

  useEffect(() => {
    if (selectedPayment === 'payNow') {
      setCreditCardFormRender(true);
    } else {
      setCreditCardFormRender(false);
    }
  }, [selectedPayment]);

  const extraContent = (
    <div className={classes.infoSelect}>
      <span className={clsx(classes.infoExtraTitle, classes.infoExtraTitleHideSm)}>
        {renderContent(
          Dictionary.airCheckoutNoPaymentTodayLabel.key,
          Dictionary.airCheckoutNoPaymentTodayLabel.defaultValue,
        )}
      </span>
      <FormControlLabel
        data-testid="reservenew-radio-btn"
        value="reserve"
        className={classes.formControlLabel}
        control={<Radio className={classes.infoRadioButton} />}
      />
    </div>
  );
  const infoOptional = (
    <div className={classes.infoWrapper}>
      <div className={clsx(classes.infoExtraTitleShowSm)}>
        <span className={classes.infoExtraTitle}>
          {renderContent(
            Dictionary.airCheckoutNoPaymentTodayLabel.key,
            Dictionary.airCheckoutNoPaymentTodayLabel.defaultValue,
          )}
        </span>
      </div>
      <div className={classes.infoDate}>
        {renderContent(
          Dictionary.airCheckoutNoPaymentTodayInfo.key,
          Dictionary.airCheckoutNoPaymentTodayInfo.defaultValue,
        )}
        &nbsp;
        {dueDate}
      </div>
    </div>
  );
  const opcionalPayment = (
    <div className={clsx(classes.info, selectedPayment === 'reserve' && classes.infoSelected)}>
      {infoOptional}
      {extraContent}
    </div>
  );

  const wrapperRequired = (
    <div className={classes.infoWrapper}>
      <div className={clsx(classes.infoExtraTitleShowSm)}>
        <span className={classes.infoExtraTitle}>{payAmount}</span>
      </div>
      {!fullPaymentDueToday && isRefundable && (
        <div className={classes.infoDate}>
          {renderContent(
            Dictionary.airCheckoutPayNowInfo.key,
            Dictionary.airCheckoutPayNowInfo.defaultValue,
          )}
          <span>&nbsp;{dueDate}</span>
        </div>
      )}
      {fullPaymentDueToday && (
        <div className={classes.infoDate}>
          {renderContent(
            Dictionary.airFullPaymentDueTodayLabel.key,
            Dictionary.airFullPaymentDueTodayLabel.defaultValue,
          )}
        </div>
      )}
    </div>
  );
  const paymentRequired = (
    <div
      className={clsx(
        classes.info,
        isPaymentRequired && classes.infoNoMarginBottom,
        selectedPayment === 'payNow' && classes.infoSelected,
      )}
      ref={checkoutRef}>
      {wrapperRequired}
      <div className={classes.infoSelect}>
        <span className={clsx(classes.infoExtraTitle, classes.infoExtraTitleHideSm)}>
          {payAmount}
        </span>
        <FormControlLabel
          data-testid="paynownew-radio-btn"
          value="payNow"
          className={classes.formControlLabel}
          control={<Radio className={classes.infoRadioButton} />}
        />
      </div>
    </div>
  );

  return (
    <div className={classes.cardContainerCheckoutWrapper}>
      <div
        className={clsx(
          classes.cardContainerCheckoutPayment,
          paymentClasses.cardContainerCheckoutPayment,
        )}>
        {!isRefund && (
          <RadioGroup
            aria-label="payment"
            name="paymentOption"
            value={selectedPayment}
            onChange={handleOnChangePaymentOption}>
            {paymentRequired}
            {!isPaymentRequired && !fullPaymentDueToday && opcionalPayment}
          </RadioGroup>
        )}
      </div>
    </div>
  );
};

PaymentOption.propTypes = {
  holdFlightData: PropTypes.shape({
    priceChange: PropTypes.shape({
      newTotal: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.string,
      }),
    }),
  }),
  extraData: PropTypes.shape({
    returnFlight: PropTypes.shape({
      cabin: PropTypes.shape({
        totalPrice: PropTypes.shape({
          value: PropTypes.number,
          currency: PropTypes.string,
        }),
        isPaymentRequired: PropTypes.bool,
        isRefundable: PropTypes.bool,
      }),
    }),
  }),
  bookingProfile: PropTypes.shape({
    flightProfile: PropTypes.shape({
      booking: PropTypes.shape({
        finalPaymentDate: PropTypes.string,
      }),
    }),
  }),
  onPropertyChage: PropTypes.func,
  isRefund: PropTypes.bool,
};

export default PaymentOption;
